import React, { useMemo } from 'react';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { Block } from '@klp/design/Block';
import { Loader } from '@klp/design/Loader';
import { Heading } from '@klp/design/Heading';
import { useTranslation } from 'react-i18next';
import MUIDataTable from 'mui-datatables';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';
import { useProsjektSammendragContext } from './context/ProsjektSammendragProvider';
import { useMapEmailToUser, useRoleMapping } from '@klp/prosjekt/hooks';

const styles = () => ({
    tableTitleContainer: {
        display: 'flex',
        alignItems: 'center',
    },
});

export const ProsjektSammendragTable: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { selectedProcess, selectedProcessIsLoading } =
        useProsjektSammendragContext();
    const { handleMapping: mapEmail } = useMapEmailToUser();
    const { handleMapping: mapRole } = useRoleMapping();

    const activities = useMemo(() => {
        const base = selectedProcess?.aktiviteter || [];
        return selectedProcess?.fullfortDato === null
            ? base
            : base.filter((a) => a.utfortDato);
    }, [selectedProcess]);

    const tableTitle = (
        <Block className={classes.tableTitleContainer}>
            <Heading
                klpHeadingLevel="TWO"
                klpColor="dark"
                translation="prosjekt.table.tableTitle"
                data-testid="ProsjektSammendragTable__Title"
            />
        </Block>
    );

    const tableColumns = useMemo<Array<MUIDataTableColumn>>(
        () => [
            {
                name: 'navn',
                label: t('prosjekt.table.oppgave'),
                options: {
                    customBodyRenderLite: (i) => activities[i].navn,
                    sort: false,
                    filter: false,
                },
            },
            {
                name: 'name',
                label: t('prosjekt.table.navn'),
                options: {
                    customBodyRenderLite: (i) =>
                        mapEmail(activities[i].tildeltTil),
                    sort: false,
                    filter: false,
                },
            },
            {
                name: 'kandidatGruppe',
                label: t('prosjekt.table.rolle'),
                options: {
                    customBodyRenderLite: (i) =>
                        mapRole(activities[i].kandidatGruppe),
                    sort: false,
                    filter: false,
                },
            },
            {
                name: 'tildeltTil',
                label: t('prosjekt.table.email'),
                options: {
                    customBodyRenderLite: (i) => activities[i].tildeltTil,
                    sort: false,
                    filter: false,
                },
            },
            {
                name: 'startDato',
                label: t('prosjekt.table.startDato'),
                options: {
                    customBodyRenderLite: (i) =>
                        formatDateWithTime(
                            parseSafeDate(activities[i].startDato),
                        ),
                    sort: false,
                    filter: false,
                },
            },
            {
                name: 'utfortDato',
                label: t('prosjekt.table.utforelseDato'),
                options: {
                    customBodyRenderLite: (i) =>
                        formatDateWithTime(
                            parseSafeDate(activities[i].utfortDato),
                        ),
                    sort: false,
                    filter: false,
                },
            },
        ],
        [activities, mapEmail, mapRole],
    );

    const tableOptions: MUIDataTableOptions = {
        search: false,
        download: false,
        print: false,
        viewColumns: false,
        filter: false,
        sort: false,
        selectableRows: 'none',
        pagination: false,
        textLabels: {
            body: {
                noMatch: t('prosjekt.table.emptyTableMessage'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (col) =>
                    t('tables.body.toolTipFor') + col.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
            selectedRows: {
                text: t('tables.selectedRows.text'),
                delete: t('tables.selectedRows.delete'),
                deleteAria: t('tables.selectedRows.deleteAria'),
            },
        },
    };

    return (
        <Loader isLoading={selectedProcessIsLoading}>
            <MUIDataTable
                title={tableTitle}
                data={activities}
                columns={tableColumns}
                options={tableOptions}
            />
        </Loader>
    );
};
